import { AppDate } from '../envTypes';

import { ModelBase } from './base';
import { Profile } from './profile';

// AvatarMe
export const AvatarMeListingName = 'Avatar Me';

export enum AvatarMeListingVariantName {
  Image = 'Image',
  Photoshoot = 'Photoshoot',
  Video5s = 'Video 5s',
  Video10s = 'Video 10s',
  VideoHdUpgrade = 'hdUpgrade',
  VideoSoundFx = 'soundFx',
  VideoFacePerfect = 'facePerfect',
  VideoLipSync = 'lipSync',
  StudioFreeImage = 'Studio Free Image',
  StudioImage = 'Studio Image',
  StudioPhotoshoot = 'Studio Photoshoot',
  StudioVideo5s = 'Studio Video 5s',
  StudioVideo10s = 'Studio Video 10s',
  StudioVideoXs = 'Studio Video Xs', // duration chosen by the buyer, priced per second
  StudioVideoPrompt = 'Studio Video Prompt',
  StudioVideoLipSync = 'Studio Video Lip Sync',
}

export enum AvatarMeListingVariantSku {
  Image = 'com.popstore.avatar_me.image',
  Photoshoot = 'com.popstore.avatar_me.photoshoot',
  Video5s = 'com.popstore.avatar_me.video.5s',
  Video10s = 'com.popstore.avatar_me.video.10s',
  VideoHdUpgrade = 'com.popstore.avatar_me.video.hd_upgrade',
  VideoSoundFx = 'com.popstore.avatar_me.video.sound_fx',
  VideoFacePerfect = 'com.popstore.avatar_me.video.face_perfect',
  VideoLipSync = 'com.popstore.avatar_me.video.lip_sync',
  StudioFreeImage = 'com.popstore.avatar_me.studio_free_image',
  StudioImage = 'com.popstore.avatar_me.studio_image',
  StudioPhotoshoot = 'com.popstore.avatar_me.studio_photoshoot',
  StudioVideo5s = 'com.popstore.avatar_me.studio_video.5s',
  StudioVideo10s = 'com.popstore.avatar_me.studio_video.10s',
  StudioVideoXs = 'com.popstore.avatar_me.studio_video.xs',
  StudioVideoPrompt = 'com.popstore.avatar_me.studio_video.prompt',
  StudioVideoLipSync = 'com.popstore.avatar_me.studio_video.lip_sync',
}

export enum AvatarMeProcessStatus {
  Initialized = 'INITIALIZED',
  Triggered = 'TRIGGERED',
  Completed = 'COMPLETED',
  PartiallyCompleted = 'PARTIALLY_COMPLETED',
  Failed = 'FAILED',
}

export enum AvatarMeImageLocationSource {
  Location = 'LOCATION',
  SceneLibrary = 'SCENE_LIBRARY',
  MySavedScene = 'MY_SAVED_SCENE',
  MyUnsavedScene = 'MY_UNSAVED_SCENE',
}

export enum AvatarMeOrientation {
  Portrait = 'PORTRAIT',
  Landscape = 'LANDSCAPE'
}

export interface AvatarMeImageRequest {
  variantSku: AvatarMeListingVariantSku.Image;
  location: string;
  locationSource?: AvatarMeImageLocationSource;
  atmosphere?: string;
  // outfitId or outfitUrl, it must have one
  outfitId?: string; // existing outfit id from creator's digital wardrobe
  outfitUrl?: string; // s3 url of creator uploaded image
  personalLookId?: string;
}

// a variant of AvatarMeImageRequest, it needs 5 prepared poses
export interface AvatarMePhotoshootRequest {
  variantSku: AvatarMeListingVariantSku.Photoshoot;
  location: string;
  locationSource?: AvatarMeImageLocationSource;
  atmosphere?: string;
  // outfitId or outfitUrl, it must have one
  outfitId?: string; // existing outfit id from creator's digital wardrobe
  outfitUrl?: string; // s3 url of creator uploaded image
  poseIds: number[];
  personalLookId?: string;
}

/**
 * deprecated
 */
export interface AvatarMeVideoRequest {
  variantSku:
    | AvatarMeListingVariantSku.Video5s
    | AvatarMeListingVariantSku.Video10s;
  imageProcessId: string;
  videoPrompt: string;
  poseUrl?: string; // aws s3(not cdn) url if imageProcessId points to a AvatarMePhotoshootRequest
  personalLookId?: string;
}

export interface AvatarMeVideo5sRequest {
  variantSku: AvatarMeListingVariantSku.Video5s;
  imageProcessId: string;
  videoPrompt: string;
  poseUrl?: string; // aws s3(not cdn) url if imageProcessId points to a AvatarMePhotoshootRequest
  personalLookId?: string;
}

export interface AvatarMeVideo10sRequest {
  variantSku: AvatarMeListingVariantSku.Video10s;
  imageProcessId: string;
  videoPrompt: string;
  poseUrl?: string; // aws s3(not cdn) url if imageProcessId points to a AvatarMePhotoshootRequest
  personalLookId?: string;
}

/**
 * deprecated
 */
export interface AvatarMeGamifiedVideoRequest {
  variantSku:
    | AvatarMeListingVariantSku.VideoHdUpgrade
    | AvatarMeListingVariantSku.VideoFacePerfect;
  videoProcessId: string;
  originalVideoProcessId?: string;
  personalLookId?: string;
}

export interface AvatarMeVideoHdUpgradeRequest {
  variantSku: AvatarMeListingVariantSku.VideoHdUpgrade;
  videoProcessId: string;
  originalVideoProcessId?: string;
  personalLookId?: string;
}

export interface AvatarMeVideoFacePerfectRequest {
  variantSku: AvatarMeListingVariantSku.VideoFacePerfect;
  videoProcessId: string;
  originalVideoProcessId?: string;
  personalLookId?: string;
}

export interface AvatarMeGamifiedLipSyncVideoRequest {
  variantSku: AvatarMeListingVariantSku.VideoLipSync;
  videoProcessId: string;
  originalVideoProcessId?: string;
  audioUrl: string;
  personalLookId?: string;
}

// creator studio is only open to creators
export interface AvatarMeStudioFreeImageRequest {
  variantSku: AvatarMeListingVariantSku.StudioFreeImage;
  outfitIds: string[]; // existing outfit id from free outfits
  wearWithAi?: {
    listingId: string;
  };
  personalLookId?: string;
}

export interface AvatarMeStudioImageRequest {
  variantSku: AvatarMeListingVariantSku.StudioImage;
  location: string;
  locationSource?: AvatarMeImageLocationSource;
  atmosphere?: string;
  // outfitId or outfitUrl, it must have one
  outfitId?: string; // existing outfit id from creator's digital wardrobe
  outfitUrl?: string; // s3 url of creator uploaded image
  poseId?: number;
  orientation?: AvatarMeOrientation;
  wearWithAi?: {
    listingId: string;
  };
  personalLookId?: string;
}

export interface AvatarMeStudioPhotoshootRequest {
  variantSku: AvatarMeListingVariantSku.StudioPhotoshoot;
  location: string;
  locationSource?: AvatarMeImageLocationSource;
  atmosphere?: string;
  // outfitId or outfitUrl, it must have one
  outfitId?: string; // existing outfit id from creator's digital wardrobe
  outfitUrl?: string; // s3 url of creator uploaded image
  poseIds: number[];
  orientation?: AvatarMeOrientation;
  wearWithAi?: {
    listingId: string;
  };
  personalLookId?: string;
}

/**
 * deprecated
 */
export interface AvatarMeStudioVideoRequest {
  variantSku:
    | AvatarMeListingVariantSku.StudioVideo5s
    | AvatarMeListingVariantSku.StudioVideo10s;
  imageProcessId: string;
  imageUrl?: string; // one of images if imageProcessId points to AvatarMeStudioFreeImageRequest or AvatarMePhotoshootRequest
  videoPrompt: string;
  wearWithAi?: {
    listingId: string;
  };
  personalLookId?: string;
}

export interface AvatarMeStudioVideo5sRequest {
  variantSku: AvatarMeListingVariantSku.StudioVideo5s;
  imageProcessId: string;
  imageUrl?: string; // one of images if imageProcessId points to AvatarMeStudioFreeImageRequest or AvatarMePhotoshootRequest
  videoPrompt: string;
  orientation?: AvatarMeOrientation;
  wearWithAi?: {
    listingId: string;
  };
  personalLookId?: string;
}

export interface AvatarMeStudioVideo10sRequest {
  variantSku: AvatarMeListingVariantSku.StudioVideo10s;
  imageProcessId: string;
  imageUrl?: string; // one of images if imageProcessId points to AvatarMeStudioFreeImageRequest or AvatarMePhotoshootRequest
  videoPrompt: string;
  orientation?: AvatarMeOrientation;
  wearWithAi?: {
    listingId: string;
  };
  personalLookId?: string;
}

/**
 * Bounds of `AvatarMeStudioVideoXsRequest.durationSeconds`, inclusive.
 */
export const AVATAR_ME_STUDIO_VIDEO_MIN_DURATION_SECONDS = 5;
export const AVATAR_ME_STUDIO_VIDEO_MAX_DURATION_SECONDS = 60;

/**
 * A studio video whose length the buyer picks, unlike StudioVideo5s/10s.
 * Priced per second (`TokenCostType.AIVideoPerSecond`): the token order's
 * variant unit price is that price x `durationSeconds`, at quantity 1.
 */
export interface AvatarMeStudioVideoXsRequest {
  variantSku: AvatarMeListingVariantSku.StudioVideoXs;
  durationSeconds: number; // whole seconds, AVATAR_ME_STUDIO_VIDEO_MIN/MAX_DURATION_SECONDS
  imageProcessId?: string;
  imageUrl?: string; // one of images if imageProcessId points to AvatarMeStudioFreeImageRequest or AvatarMePhotoshootRequest
  videoPrompt: string;
  orientation?: AvatarMeOrientation;
  wearWithAi?: {
    listingId: string;
  };
  personalLookId?: string;
}

export interface AvatarMeStudioVideoPromptRequest {
  variantSku: AvatarMeListingVariantSku.StudioVideoPrompt;
  imageProcessId: string;
  imageUrl?: string; // one of images if imageProcessId points to AvatarMeStudioFreeImageRequest or AvatarMePhotoshootRequest
  videoPrompt: string; // a video-type prompt
  orientation?: AvatarMeOrientation;
  wearWithAi?: {
    listingId: string;
  };
  personalLookId?: string;
}

export interface AvatarMeStudioGamifiedLipSyncVideoRequest {
  variantSku: AvatarMeListingVariantSku.StudioVideoLipSync;
  videoProcessId: string;
  originalVideoProcessId?: string;
  audioUrl: string;
  orientation?: AvatarMeOrientation;
  personalLookId?: string;
}

export type AvatarMeRequest =
  | AvatarMeImageRequest
  | AvatarMePhotoshootRequest
  | AvatarMeVideo5sRequest
  | AvatarMeVideo10sRequest
  | AvatarMeVideoHdUpgradeRequest
  | AvatarMeVideoFacePerfectRequest
  | AvatarMeGamifiedLipSyncVideoRequest
  | AvatarMeStudioImageRequest
  | AvatarMeStudioPhotoshootRequest
  | AvatarMeStudioVideo5sRequest
  | AvatarMeStudioVideo10sRequest
  | AvatarMeStudioVideoXsRequest
  | AvatarMeStudioVideoPromptRequest
  | AvatarMeStudioGamifiedLipSyncVideoRequest;


// AI-generated PDF
export const AiGeneratedPdfListingName = 'AI-Genrated PDF';

export enum AiGeneratedPdfListingVariantName {
  FullyAuto = 'Fullly Automatic',
  AiAssisted = 'AI Assisted',
  EditPdf = 'Edit-PDF'
}

export enum AiGeneratedPdfListingVariantSku {
  FullyAuto = 'com.popstore.ai_generated_pdf.fully_auto',
  AiAssisted = 'com.popstore.ai_generated_pdf.ai_assisted',
  EditPdf = 'com.popstore.ai_generated_pdf.edit_pdf',
}

export interface AiGeneratedPdfFullyAutoRequest {
  variantSku: AiGeneratedPdfListingVariantSku.FullyAuto;
}

export interface AiGeneratedPdfAiAssistedRequest {
  variantSku: AiGeneratedPdfListingVariantSku.AiAssisted;
}

export interface AiGeneratedPdfEditPdfRequest {
  variantSku: AiGeneratedPdfListingVariantSku.EditPdf;
}

// echo-for-commentchat
export const EchoForCommentchatListingName = 'Echo-for-Commentchat';

export enum EchoForCommentchatListingVariantName {
  AiDmResponse = 'AI DM Response',
  AICommentResponse = 'AI Comment Response',
  EchoCommentGeneration = 'Echo Comment Generation',
  DmOfferAnalysis = 'DM Offer Analysis',
  AiCommentAnalysis = 'AI Comment Analysis',
  AiPostAnalysis = 'AI Post Analysis',
  AiCommentIntentOfferMatching = 'AI Comment Intent Offer Matching',
  FollowerProfileScrape = 'Follower Profile Scrape',
  CollabPostCommentScrape = 'Collab Post Comment Scrape',
  GrowthRxDiagnostic = 'Growth RX Diagnostic',
  GrowthRxScript = 'Growth RX Script Generation',
  GrowthRxPostScore = 'Growth RX Post Score',
}

export enum EchoForCommentchatListingVariantSku {
  AiDmResponse = 'com.popstore.echo_for_commentchat.ai_dm_response',
  AiCommentResponse = 'com.popstore.echo_for_commentchat.ai_comment_response',
  EchoCommentGeneration = 'com.popstore.echo_for_commentchat.echo_comment_generation',
  DmOfferAnalysis = 'com.popstore.echo_for_commentchat.dm_offer_analysis',
  AiCommentAnalysis = 'com.popstore.echo_for_commentchat.ai_comment_analysis',
  AiPostAnalysis = 'com.popstore.echo_for_commentchat.ai_post_analysis',
  AiCommentIntentOfferMatching = 'com.popstore.echo_for_commentchat.ai_comment_intent_offer_matching',
  FollowerProfileScrape = 'com.popstore.echo_for_commentchat.follower_profile_scrape',
  /** A CONTRACT with popstore-ai-service (`TOKEN_VARIANT_SKU.COLLAB_POST_COMMENT_SCRAPE`), which carries it as a literal. */
  CollabPostCommentScrape = 'com.popstore.echo_for_commentchat.collab_post_comment_scrape',
  /**
   * Growth RX. These two strings are a CONTRACT with popstore-ai-service, which
   * carries them as literals in `TOKEN_VARIANT_SKU` (`src/agents/sea/_seaHelpers.ts`)
   * — it does not import this enum. A rename here that is not mirrored there
   * produces an unknown SKU, the order fails, and BOTH of that service's helpers
   * swallow it (`checkCanAffordToken` returns true on throw, `placeTokenOrder`
   * only logs), so the charge silently stops happening.
   */
  GrowthRxDiagnostic = 'com.popstore.growth_rx.diagnostic_run',
  GrowthRxScript = 'com.popstore.growth_rx.script_generation',
  /** Same contract as the two above: `TOKEN_VARIANT_SKU.GROWTH_RX_POST_SCORE` in popstore-ai-service. */
  GrowthRxPostScore = 'com.popstore.growth_rx.post_score',
}

export interface EchoForCommentchatAiDmResponseRequest {
  variantSku: EchoForCommentchatListingVariantSku.AiDmResponse;
}

export interface EchoForCommentchatAiCommentResponseRequest {
  variantSku: EchoForCommentchatListingVariantSku.AiCommentResponse;
}

export interface EchoForCommentchatEchoCommentGenerationRequest {
  variantSku: EchoForCommentchatListingVariantSku.EchoCommentGeneration;
}

export interface EchoForCommentchatDmOfferAnalysisRequest {
  variantSku: EchoForCommentchatListingVariantSku.DmOfferAnalysis;
}

export interface EchoForCommentchatAiCommentAnalysisRequest {
  variantSku: EchoForCommentchatListingVariantSku.AiCommentAnalysis;
}

export interface EchoForCommentchatAiPostAnalysisRequest {
  variantSku: EchoForCommentchatListingVariantSku.AiPostAnalysis;
}

export interface EchoForCommentchatAiCommentIntentOfferMatchingRequest {
  variantSku: EchoForCommentchatListingVariantSku.AiCommentIntentOfferMatching;
}

export interface EchoForCommentchatFollowerProfileScrapeRequest {
  variantSku: EchoForCommentchatListingVariantSku.FollowerProfileScrape;
}

export interface EchoForCommentchatCollabPostCommentScrapeRequest {
  variantSku: EchoForCommentchatListingVariantSku.CollabPostCommentScrape;
}

export interface EchoForCommentchatGrowthRxDiagnosticRequest {
  variantSku: EchoForCommentchatListingVariantSku.GrowthRxDiagnostic;
}

export interface EchoForCommentchatGrowthRxScriptRequest {
  variantSku: EchoForCommentchatListingVariantSku.GrowthRxScript;
}

export interface EchoForCommentchatGrowthRxPostScoreRequest {
  variantSku: EchoForCommentchatListingVariantSku.GrowthRxPostScore;
}

// purchased AIGC
export enum PurchasedAvatarMeMediaSourceType {
  // money-to-token order
  Order = 'ORDER',
  // consuming-token order
  TokenOrder = 'TOKEN_ORDER',
  VidCon2026 = 'VID_CON_2026',
}

// doc is is request/process id
export interface PurchasedAvatarMeMedia extends ModelBase {
  buyerId: string;
  buyerProfile?: Pick<
    Profile,
    'avatar' | 'username' | 'firstName' | 'lastName'
  >;
  sourceType?: PurchasedAvatarMeMediaSourceType;
  orderId?: string;
  storeId: string;
  storeName: string;
  listingId?: string;
  request:
    | AvatarMeImageRequest
    | AvatarMePhotoshootRequest
    | AvatarMeVideo5sRequest
    | AvatarMeVideo10sRequest
    | AvatarMeVideoHdUpgradeRequest
    | AvatarMeVideoFacePerfectRequest
    | AvatarMeGamifiedLipSyncVideoRequest
    | AvatarMeStudioFreeImageRequest
    | AvatarMeStudioImageRequest
    | AvatarMeStudioPhotoshootRequest
    | AvatarMeStudioVideo5sRequest
    | AvatarMeStudioVideo10sRequest
    | AvatarMeStudioVideoXsRequest
    | AvatarMeStudioVideoPromptRequest
    | AvatarMeStudioGamifiedLipSyncVideoRequest;
  status: AvatarMeProcessStatus;
  personalLookId?: string;
  completedAt?: AppDate;
  failedAt?: AppDate;
  failureReason?: string;
  refunded?: boolean; // true when refunded
  refundedAt?: AppDate;
  watermarkUrls?: string[]; // original watermark urls from commentsold
  cdnUrls?: string[]; // original cnd urls from commentsold
  noWatermarkCdnUrls?: string[]; // original no watermark cnd urls from commentsold
  originalUrl?: string; // original url from commentsold
  // if type is IMAGE, it's a firestore storage url with query parameters(id={imageId}, source=avatarme) and blurhash
  url?: string;
  webhook?: {
    timestamp: AppDate;
    payload: any;
  };
  wearWithAi?: {
    listingId: string;
    // visible to customers, true as default
    visible: boolean;
    // make it as cover image/video, false as default except for first generated video for this listing
    favorite: boolean;
  };
  // timestamp when the seller checks this seller's own generation(StudioFreeImage for now)
  checkedAtBySeller?: AppDate;
}
